/*
 * Carrying on when the Wi-Fi drops.
 *
 * Care home corridors have dead spots, and a medicines round does not stop at one. So a dose
 * recorded with no signal is written to the tablet, shown as recorded, and sent when the
 * connection comes back.
 *
 * Three rules make that safe rather than merely convenient:
 *
 *   1. Every record gets an id made on the tablet before anything is sent. If the tablet does not
 *      hear back it sends the same one again, and the server answers with the record it already
 *      made. A retry can never become a second dose.
 *   2. The time the carer recorded it travels with the record, so the chart shows when the dose was
 *      given rather than when the signal came back.
 *   3. A refusal is not the same as a dropped connection. "Already recorded by someone else" means
 *      the tablet must stop and tell the carer; only a network failure is worth retrying.
 *
 * Nothing here decides anything clinical. The server checks every rule again when the record
 * arrives, because a tablet's copy of the truth is always a little out of date.
 */

const DB_NAME = 'ayp-connect';
const DB_VERSION = 1;
const OUTBOX = 'outbox';
const CACHE = 'cache';

export type Outgoing = {
  id: string;
  url: string;
  body: Record<string, unknown>;
  what: string;
  addedAt: number;
  tries: number;
  /* Who recorded it. It is only ever sent under their login — see flush(). */
  by: string;
  byName: string;
  /* Set when the server refused it outright. The carer has to be told; retrying will not help. */
  refused?: string;
};

/*
 * Who is signed in on this tablet. A record is sent under the login of the person who made it and
 * nobody else's: if Sam recorded a dose with no signal and Priya signed in before it was sent, it
 * must not go up as Priya's. It waits for Sam.
 */
let signedIn: { id: string; name: string } | null = null;

export function setSignedIn(user: { id: string; name: string } | null): void {
  signedIn = user;
  notify();
  if (user) void flush();
}

export function whoIsSignedIn(): { id: string; name: string } | null {
  return signedIn;
}

let db: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (!db) {
    db = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(OUTBOX)) database.createObjectStore(OUTBOX, { keyPath: 'id' });
        if (!database.objectStoreNames.contains(CACHE)) database.createObjectStore(CACHE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return db;
}

async function run<T>(store: string, mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await open();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, mode);
    const request = work(transaction.objectStore(store));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/* ---------- The outbox ---------- */

export async function queue(item: Omit<Outgoing, 'addedAt' | 'tries' | 'by' | 'byName'>): Promise<void> {
  const who = signedIn ?? { id: '', name: 'somebody' };
  await run(OUTBOX, 'readwrite', (store) =>
    store.put({ ...item, addedAt: Date.now(), tries: 0, by: who.id, byName: who.name }),
  );
  notify();
}

export async function outbox(): Promise<Outgoing[]> {
  const all = await run<Outgoing[]>(OUTBOX, 'readonly', (store) => store.getAll() as IDBRequest<Outgoing[]>);
  return all.sort((a, b) => a.addedAt - b.addedAt);
}

export async function forget(id: string): Promise<void> {
  await run(OUTBOX, 'readwrite', (store) => store.delete(id) as unknown as IDBRequest<undefined>);
  notify();
}

async function update(item: Outgoing): Promise<void> {
  await run(OUTBOX, 'readwrite', (store) => store.put(item));
  notify();
}

/* ---------- What the round needs to keep working ---------- */

/*
 * The last answer the server gave for a screen, so the round can be opened in a corridor with no
 * signal. It is only ever used when the network has actually failed, and the screen says when it
 * was last fetched — stale medicines information is dangerous if it is passed off as current.
 */
export async function remember(key: string, value: unknown): Promise<void> {
  await run(CACHE, 'readwrite', (store) => store.put({ value, at: Date.now() }, key));
}

export async function recall<T>(key: string): Promise<{ value: T; at: number } | null> {
  const found = await run<{ value: T; at: number } | undefined>(
    CACHE,
    'readonly',
    (store) => store.get(key) as IDBRequest<{ value: T; at: number } | undefined>,
  );
  return found ?? null;
}

/*
 * Which doses are sitting on this tablet waiting to go. The round screen needs this so it can show
 * them as recorded — a carer who has just signed for a dose must not be shown it as still due, or
 * the next person along will give it again.
 */
export async function waiting(): Promise<Map<string, Outgoing>> {
  const out = new Map<string, Outgoing>();
  for (const item of await outbox()) {
    const body = item.body as {
      medicineId?: string; dueOn?: string; dueAt?: string;
      items?: Array<{ medicineId: string }>;
    };
    if (body.medicineId && body.dueOn && body.dueAt) {
      out.set(`${body.medicineId}|${body.dueOn}|${body.dueAt}`, item);
    }
    // A tray slot is one send holding several doses.
    for (const line of body.items ?? []) {
      if (body.dueOn && body.dueAt) out.set(`${line.medicineId}|${body.dueOn}|${body.dueAt}`, item);
    }
  }
  return out;
}

/* ---------- Sending ---------- */

let sending = false;

/*
 * Empty the outbox, oldest first. Order matters: a tray slot recorded before a correction has to
 * reach the server in that order or the correction has nothing to correct.
 */
export async function flush(): Promise<{ sent: number; refused: number; waiting: number }> {
  if (sending || !navigator.onLine) {
    const waiting = (await outbox()).filter((item) => !item.refused).length;
    return { sent: 0, refused: 0, waiting };
  }

  sending = true;
  let sent = 0;
  try {
    for (const item of await outbox()) {
      if (item.refused) continue;
      // Not this person's record. It waits until they sign in.
      if (!signedIn || item.by !== signedIn.id) continue;

      let response: Response;
      try {
        response = await fetch(item.url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(item.body),
        });
      } catch {
        // Still no connection. Leave it where it is and try again later.
        await update({ ...item, tries: item.tries + 1 });
        break;
      }

      if (response.ok) {
        await forget(item.id);
        sent += 1;
        continue;
      }

      if (response.status === 401 || response.status >= 500) {
        // Signed out, or the server is having a moment. Neither is the carer's fault; try later.
        await update({ ...item, tries: item.tries + 1 });
        break;
      }

      // A real refusal — a rule the server would not bend, or a login that is no longer allowed
      // to record. Keep it, and make sure somebody sees it.
      const text = await response.text().catch(() => '');
      const message = readError(text);
      await update({ ...item, tries: item.tries + 1, refused: message });
    }
  } finally {
    sending = false;
  }

  const left = await outbox();
  notify();
  return { sent, refused: left.filter((item) => item.refused).length, waiting: left.filter((i) => !i.refused).length };
}

function readError(text: string): string {
  try {
    const parsed = JSON.parse(text) as { error?: string };
    return parsed.error ?? 'The server would not accept this.';
  } catch {
    return 'The server would not accept this.';
  }
}

/* ---------- Telling the screens ---------- */

type Listener = () => void;
const listeners = new Set<Listener>();

export function onOutboxChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

/* Try again whenever the tablet thinks it is back, and every half minute regardless. */
export function startSending(): void {
  window.addEventListener('online', () => void flush());
  window.setInterval(() => void flush(), 30_000);
  void flush();
}
